// Stock Adjustment Dialog for inventory management

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

interface StockAdjustmentDialogProps {
  open: boolean;
  product: any | null;
  onClose: () => void;
  onSave: (productId: string, newStock: number, adjustment: StockAdjustment) => void;
}

interface StockAdjustment {
  type: 'increase' | 'decrease' | 'set';
  amount: number;
  reason: string;
  notes?: string;
}

const adjustmentReasons = {
  increase: [
    'Dostawa towaru',
    'Zwrot od klienta',
    'Korekta inwentaryzacyjna',
    'Przeniesienie z innego magazynu',
    'Inne'
  ],
  decrease: [
    'Sprzedaż/Wydanie',
    'Uszkodzenie produktu',
    'Utrata/Kradzież',
    'Przeniesienie do innego magazynu',
    'Korekta inwentaryzacyjna',
    'Data ważności',
    'Inne'
  ],
  set: [
    'Inwentaryzacja pełna',
    'Korekta systemu',
    'Inizjalizacja stanu',
    'Inne'
  ]
};

export default function StockAdjustmentDialog({ 
  open, 
  product, 
  onClose, 
  onSave 
}: StockAdjustmentDialogProps) {
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease' | 'set'>('decrease');
  const [amount, setAmount] = useState<number>(1);
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const handleClose = () => {
    // Reset form
    setAdjustmentType('decrease');
    setAmount(1);
    setReason('');
    setNotes('');
    onClose();
  };

  const handleSave = () => {
    if (!product || !reason) return;

    let newStock = product.currentStock;
    
    switch (adjustmentType) {
      case 'increase':
        newStock = product.currentStock + amount;
        break;
      case 'decrease':
        newStock = Math.max(0, product.currentStock - amount);
        break;
      case 'set':
        newStock = amount;
        break;
    }

    const adjustment: StockAdjustment = {
      type: adjustmentType,
      amount,
      reason,
      notes: notes || undefined
    };

    onSave(product.id, newStock, adjustment);
    handleClose();
  };

  const getNewStock = () => {
    if (!product) return 0;
    
    switch (adjustmentType) {
      case 'increase':
        return product.currentStock + amount;
      case 'decrease':
        return Math.max(0, product.currentStock - amount);
      case 'set':
        return amount;
      default:
        return product.currentStock;
    }
  };

  const getStatusColor = (stock: number) => {
    if (!product) return 'default';
    if (stock <= product.minLevel) return 'error';
    if (stock <= product.minLevel * 1.5) return 'warning';
    return 'success';
  };

  const getStatusLabel = (stock: number) => {
    if (!product) return '';
    if (stock <= product.minLevel) return 'Krytyczny';
    if (stock <= product.minLevel * 1.5) return 'Niski';
    return 'OK';
  };

  if (!product) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <EditIcon color="primary" />
          <Typography variant="h6">
            Korekta stanu magazynowego
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box>
          {/* Product Info */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {product.name}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Chip 
                label={product.category} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={`Aktualny stan: ${product.currentStock} szt`}
                color={getStatusColor(product.currentStock) as any}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Adjustment Type */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Typ korekty</InputLabel>
                <Select
                  value={adjustmentType}
                  label="Typ korekty"
                  onChange={(e) => setAdjustmentType(e.target.value as any)}
                >
                  <MenuItem value="decrease">
                    <Box display="flex" alignItems="center" gap={1}>
                      <RemoveIcon fontSize="small" />
                      Zmniejsz stan (wydanie)
                    </Box>
                  </MenuItem>
                  <MenuItem value="increase">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon fontSize="small" />
                      Zwiększ stan (dostawa)
                    </Box>
                  </MenuItem>
                  <MenuItem value="set">
                    <Box display="flex" alignItems="center" gap={1}>
                      <EditIcon fontSize="small" />
                      Ustaw dokładny stan
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Amount */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={adjustmentType === 'set' ? 'Nowy stan' : 'Ilość'}
                type="number"
                value={amount}
                onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
                inputProps={{ 
                  min: 0,
                  max: adjustmentType === 'decrease' ? product.currentStock : undefined
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Stan po zmianie"
                value={`${getNewStock()} szt`}
                disabled
                InputProps={{
                  endAdornment: (
                    <Chip 
                      label={getStatusLabel(getNewStock())}
                      color={getStatusColor(getNewStock()) as any}
                      size="small"
                    />
                  )
                }}
              />
            </Grid>
          </Grid>

          {/* Reason */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Powód korekty *</InputLabel>
                <Select
                  value={reason}
                  label="Powód korekty *"
                  onChange={(e) => setReason(e.target.value)}
                  required
                >
                  {adjustmentReasons[adjustmentType].map((reasonOption) => (
                    <MenuItem key={reasonOption} value={reasonOption}>
                      {reasonOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Notes */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dodatkowe uwagi (opcjonalne)"
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Dodatkowe informacje o korekcie..."
              />
            </Grid>
          </Grid>

          {/* Warning for critical stock */}
          {getNewStock() <= product.minLevel && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Uwaga:</strong> Po tej korekcie produkt będzie miał stan krytyczny 
                (poniżej minimum {product.minLevel} szt). Rozważ złożenie zamówienia.
              </Typography>
            </Alert>
          )}

          {/* Summary */}
          <Box 
            sx={{ 
              bgcolor: 'grey.50', 
              p: 2, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Podsumowanie korekty:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Stan obecny:</strong> {product.currentStock} szt<br/>
              <strong>Stan po korekcie:</strong> {getNewStock()} szt<br/>
              <strong>Zmiana:</strong> {adjustmentType === 'increase' ? '+' : adjustmentType === 'decrease' ? '-' : '='}{adjustmentType === 'set' ? getNewStock() : amount} szt<br/>
              <strong>Powód:</strong> {reason || 'Nie wybrano'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          startIcon={<CancelIcon />}
        >
          Anuluj
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!reason}
          startIcon={<SaveIcon />}
        >
          Zapisz korektę
        </Button>
      </DialogActions>
    </Dialog>
  );
}